import type { AppProfile, ModelLists, Segment } from '../types'
import { usesRifeBridge } from './servers'

const isRealLora = (name: string): boolean => Boolean(name && name.trim().toLowerCase() !== 'none')

const resolvedSegments = (profile: AppProfile): Segment[] =>
  profile.mode === 'simple' ? [profile.simple_segment] : profile.segments

export function validateGeneration(
  profile: AppProfile,
  connected: boolean,
  models: ModelLists,
  hasStartImage: boolean,
): string[] {
  const errors: string[] = []
  const { gs } = profile
  if (!connected) errors.push('利用可能な ComfyUI Server に接続してください。')
  if (!hasStartImage && !gs.start_image) errors.push('Start Image を指定してください。')
  const requiredModels: Array<[string, string]> = [
    ['High Noise Model', gs.unet_high],
    ['Low Noise Model', gs.unet_low],
    ['Text Encoder', gs.clip],
    ['VAE', gs.vae],
    ['CLIP Vision', gs.clip_vision],
  ]
  requiredModels.forEach(([label, value]) => {
    if (!value) errors.push(`${label} を指定してください。`)
  })
  if (connected && !models.samplers.includes(gs.sampler)) {
    errors.push(`Sampler '${gs.sampler}' は接続中の ComfyUI にありません。`)
  }
  if (connected && !models.schedulers.includes(gs.scheduler)) {
    errors.push(`Scheduler '${gs.scheduler}' は接続中の ComfyUI にありません。`)
  }
  if (connected && usesRifeBridge(profile) && !models.rife.includes(gs.rife_model)) {
    errors.push(models.rife.length
      ? `RIFE Model '${gs.rife_model}' は接続中の ComfyUI にありません。`
      : 'Loop Bridge に必要な RIFE VFI node / model が見つかりません。')
  }

  const segments = resolvedSegments(profile)
  if (profile.mode === 'multi') {
    if (segments.length < 2) errors.push('Multi は最低 2 Segment 必要です。')
    if (!['i2v', 'flf2v'].includes(segments[0]?.node_type)) {
      errors.push('先頭 Segment は I2V または FLF2V にしてください。')
    }
    segments.slice(1).forEach((segment, index) => {
      if (!['flf2v', 'svi', 'sviflf'].includes(segment.node_type)) {
        errors.push(`Segment ${index + 2} は FLF2V、SVI または SVIFLF にしてください。`)
      }
    })
    if (segments.some((segment) => ['svi', 'sviflf'].includes(segment.node_type))) {
      if (!isRealLora(gs.svi_lora_high)) errors.push('SVI LoRA High を指定してください。')
      if (!isRealLora(gs.svi_lora_low)) errors.push('SVI LoRA Low を指定してください。')
    }
  } else if (!['i2v', 'flf2v'].includes(profile.simple_segment.node_type)) {
    errors.push('Simple の Node type は I2V または FLF2V にしてください。')
  }

  if (connected && models.lora.length > 1) {
    const known = new Set(models.lora)
    const referenced = [gs.accel_lora, gs.svi_lora_high, gs.svi_lora_low]
    referenced.push(...gs.global_lora_high.map((lora) => lora.name))
    referenced.push(...gs.global_lora_low.map((lora) => lora.name))
    for (const segment of segments) {
      referenced.push(...segment.lora_high.map((lora) => lora.name))
      referenced.push(...segment.lora_low.map((lora) => lora.name))
    }
    referenced.filter(isRealLora).forEach((name) => {
      if (!known.has(name)) errors.push(`LoRA '${name}' は現在の ComfyUI にありません。`)
    })
  }
  return [...new Set(errors)]
}
